import FormInput from '@form/FormInput';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StyledInput } from '@ui';
import { cx } from 'class-variance-authority';
import Joi from 'joi';
import { useState } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';
import { useController, useFormContext } from 'react-hook-form';

interface Props {
    label: string;
    name: string;
    required?: boolean;
    inputProps?: any;
}

export const getAddressValidation = (name: string = 'address', required: boolean = false) =>
    Joi.object({
        street: required ? Joi.string().min(1).required() : Joi.string().allow('', null).optional(),
        streetnumber: required ? Joi.string().min(1).required() : Joi.string().allow('', null).optional(),
        city: required ? Joi.string().min(1).required() : Joi.string().allow('', null).optional(),
        postalcode: required ? Joi.string().min(1).required() : Joi.string().allow('', null).optional(),
        // Optional extras stored by Google Places
        country: Joi.string().allow('', null).optional(),
        longitude: Joi.number().allow(null).optional(),
        latitude: Joi.number().allow(null).optional(),
    });

const FormAddress = ({ label, name, required = false, inputProps }: Props) => {
    const [manualMode, setManualMode] = useState(false);
    const { control } = useFormContext();
    const {
        field: { value, onChange, onBlur },
        fieldState: { error }
    } = useController({ name, control });
    const currentAddress = value ?? {};
    const errorMessage = error?.message;

    const getAddressComponent = (type: string, address_components: any, short = false) => {
        let name = '';
        if (address_components) {
            address_components.forEach((val) => {
                if (val.types.includes(type)) {
                    name = short ? val.short_name : val.long_name;
                }
            });
        }
        return name;
    };

    const handlePlaceSelect = (address) => {
        const addressObject = address.address_components;
        onChange({
            street: getAddressComponent('route', addressObject),
            streetnumber: getAddressComponent('street_number', addressObject),
            city: getAddressComponent('locality', addressObject),
            postalcode: getAddressComponent('postal_code', addressObject),
            country: getAddressComponent('country', addressObject, true),
            longitude: address.geometry?.location.lng(),
            latitude: address.geometry?.location.lat()
        });
    };

    const noAddress = Object.values(currentAddress).filter((v) => v).length <= 0;
    const noCompleteAddress =
        Object.entries(currentAddress).filter(
            ([key, value]) => !!value && !['longitude', 'latitude', 'country'].includes(key)
        ).length < 4 && !noAddress;

    const { ref } = usePlacesWidget({
        apiKey: process.env.NEXT_PUBLIC_MAPS_API_KEY,
        onPlaceSelected: handlePlaceSelect,
        options: { types: [] },
        language: 'de'
    });

    return (
        <div className="tw-mb-4">
            <StyledInput
                onBlur={onBlur}
                animated={false}
                placeholder="Gebe hier die neue Adresse ein ..."
                ref={ref}
                disabled={manualMode}
                fullWidth
                type="text"
                label={label}
                css={{ mb: inputProps?.css?.pb || errorMessage ? '$10' : '$4', mt: '$4' }}
                isInvalid={!!errorMessage}
                helperText={errorMessage || inputProps?.helperText}
                onKeyDown={(e) => {
                    if (e.which == 13) e.preventDefault();
                }}
            />
            <div
                className={cx(
                    'tw-card-bordered tw-my-2 tw-flex tw-items-start tw-justify-between tw-gap-2 tw-p-2',
                    (errorMessage || noCompleteAddress) && 'tw-bg-red-200 tw-text-red-600'
                )}
            >
                <div>
                    {!noAddress ? (
                        <>
                            <span className="tw-block">
                                {currentAddress.street} {currentAddress.streetnumber}
                            </span>
                            <span className="tw-block">
                                {currentAddress.postalcode} {currentAddress.city}
                            </span>
                        </>
                    ) : (
                        <p>Bitte gebe eine gültige Adresse in das obige Feld ein.</p>
                    )}
                </div>
                <button
                    type="button"
                    className="tw-button-clean tw-text-accents-6"
                    onClick={() => setManualMode(!manualMode)}
                >
                    <FontAwesomeIcon icon={manualMode ? faLockOpen : faLock} />
                </button>
            </div>
            {manualMode && (
                <div>
                    <FormInput name={`${name}.street`} label="Straße" />
                    <FormInput name={`${name}.streetnumber`} label="Hausnummer" />
                    <FormInput name={`${name}.postalcode`} label="Postleitzahl" />
                    <FormInput name={`${name}.city`} label="Stadt" />
                </div>
            )}
            {noCompleteAddress && <p className="tw-m-0 tw-text-error">Bitte gebe eine vollständige Adresse an.</p>}
        </div>
    );
};

export default FormAddress;
